import { ExternalLink, Loader2, RefreshCw, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { NotionConnection } from "@/lib/types"

const INTEGRATIONS_URL = "https://www.notion.so/profile/integrations"

/** Connect one Notion workspace to the agent by pasting an internal-integration token. */
export function NotionPanel({ agentId }: { agentId: string }) {
  const t = useT()
  const [loading, setLoading] = React.useState(true)
  const [connection, setConnection] = React.useState<NotionConnection | null>(null)
  const [token, setToken] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [replacing, setReplacing] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    api
      .getNotionConnection(agentId)
      .then((row) => {
        if (!cancelled) setConnection(row)
      })
      .catch(() => toast.error(t("notionPanel.toasts.loadFailed")))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [agentId, t])

  async function connect() {
    const value = token.trim()
    if (!value) return
    setBusy(true)
    try {
      const row = await api.saveNotionConnection(agentId, value)
      setConnection(row)
      setToken("")
      setReplacing(false)
      if (row.status === "ok") {
        toast.success(t("notionPanel.toasts.connected", { workspace: row.workspace_name ?? "Notion" }))
      } else {
        toast.error(row.last_error ?? t("notionPanel.toasts.connectFailed"))
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("notionPanel.toasts.connectFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function retest() {
    setBusy(true)
    try {
      const row = await api.testNotionConnection(agentId)
      setConnection(row)
      if (row.status === "ok") toast.success(t("notionPanel.toasts.testOk"))
      else toast.error(row.last_error ?? t("notionPanel.toasts.testFailed"))
    } catch {
      toast.error(t("notionPanel.toasts.testFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    if (!window.confirm(t("notionPanel.removeConfirm"))) return
    setBusy(true)
    try {
      await api.deleteNotionConnection(agentId)
      setConnection(null)
    } catch {
      toast.error(t("notionPanel.toasts.removeFailed"))
    } finally {
      setBusy(false)
    }
  }

  const showForm = connection === null || replacing

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("notionPanel.title")}</CardTitle>
        <CardDescription>{t("notionPanel.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" />
            {t("notionPanel.loading")}
          </p>
        ) : (
          <>
            {connection && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">
                    {connection.workspace_name ?? t("notionPanel.unknownWorkspace")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("notionPanel.tokenHint", { last4: connection.token_last4 })}
                  </p>
                  {connection.status === "error" && connection.last_error && (
                    <p className="text-destructive mt-1 text-xs">{connection.last_error}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {connection.status === "error" ? (
                    <Badge variant="destructive">{t("notionPanel.status.error")}</Badge>
                  ) : (
                    <Badge variant="secondary">{t("notionPanel.status.ok")}</Badge>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => void retest()}
                    aria-label={t("notionPanel.testAria")}
                  >
                    <RefreshCw className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    onClick={() => void disconnect()}
                    aria-label={t("notionPanel.removeAria")}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            )}

            {showForm ? (
              <div className="space-y-2">
                <Label htmlFor="notion-token">{t("notionPanel.tokenLabel")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="notion-token"
                    type="password"
                    autoComplete="off"
                    placeholder="ntn_…"
                    value={token}
                    onChange={(event) => setToken(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void connect()
                    }}
                  />
                  <Button type="button" disabled={busy || !token.trim()} onClick={() => void connect()}>
                    {busy && <Loader2 className="size-4 animate-spin" />}
                    {t("notionPanel.connectButton")}
                  </Button>
                  {replacing && (
                    <Button type="button" variant="ghost" onClick={() => setReplacing(false)}>
                      {t("notionPanel.cancelButton")}
                    </Button>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {t("notionPanel.help")}{" "}
                  <a
                    href={INTEGRATIONS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 underline"
                  >
                    notion.so/profile/integrations
                    <ExternalLink className="size-3" />
                  </a>
                </p>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => setReplacing(true)}>
                {t("notionPanel.replaceButton")}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
